import React from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SocialProofCounter } from "@/components/SocialProofCounter";
import { Button } from "@/components/ui/button";
import { MapPin, Search, Users, Handshake, Sparkles, ArrowRight, Store, Camera } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1759393852314-59dc00faeed3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Pilot banner */}
      <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 px-4 py-2.5 text-center text-xs font-medium text-white sm:text-sm" data-testid="pilot-banner">
        <MapPin className="h-4 w-4 shrink-0" />
        We're launching across the UK — free for founding businesses &amp; creators during our pilot.
      </div>

      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:py-20">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" /> Local creator marketplace
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-none tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl">
              Find influence <span className="text-emerald-600">closer to home.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-zinc-600 sm:text-lg">
              Connect with local creators who can put your business in front of the people that matter.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                data-testid="hero-business-btn"
                onClick={() => navigate("/auth?mode=register&role=business")}
                className="h-12 rounded-full bg-emerald-600 px-7 text-base hover:bg-emerald-700"
              >
                <Store className="mr-2 h-5 w-5" /> I'm a Business
              </Button>
              <Button
                data-testid="hero-creator-btn"
                onClick={() => navigate("/auth?mode=register&role=creator")}
                variant="outline"
                className="h-12 rounded-full border-2 border-zinc-900 px-7 text-base hover:bg-zinc-900 hover:text-white"
              >
                <Camera className="mr-2 h-5 w-5" /> I'm a Creator
              </Button>
            </div>
            <button
              data-testid="hero-browse-btn"
              onClick={() => navigate("/discover")}
              className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-zinc-500 hover:text-emerald-600"
            >
              Browse local creators <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-4 text-sm text-zinc-500">
              Already a member?{" "}
              <button
                data-testid="hero-login-link"
                onClick={() => navigate("/auth?mode=login")}
                className="font-semibold text-emerald-600 underline underline-offset-2 hover:text-emerald-700"
              >
                Log in
              </button>
            </p>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative overflow-hidden rounded-3xl border border-zinc-200 shadow-2xl">
              <img src={HERO_IMG} alt="Local creator" className="h-[420px] w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-5">
                <div className="rounded-2xl bg-white/95 p-4 backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Real influence</p>
                  <p className="mt-1 font-display text-lg font-bold text-zinc-900">Local creators. Local businesses.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SocialProofCounter />

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">How it works</p>
          <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-zinc-900 sm:text-3xl lg:text-4xl">
            Three simple steps to real collaborations
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { Icon: Search, title: "Discover", body: "Find relevant creators near your business.", tint: "bg-emerald-100 text-emerald-600" },
            { Icon: Users, title: "Connect", body: "See their audience, content and prices.", tint: "bg-indigo-100 text-indigo-600" },
            { Icon: Handshake, title: "Collaborate", body: "Send them a campaign opportunity directly.", tint: "bg-amber-100 text-amber-600" },
          ].map((s, i) => (
            <div key={s.title} className="animate-fade-up rounded-3xl border border-zinc-200 bg-white p-7 shadow-sm" style={{ animationDelay: `${i * 80}ms` }}>
              <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${s.tint}`}>
                <s.Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold text-zinc-900">{s.title}</h3>
              <p className="mt-1.5 text-zinc-600">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* For businesses / creators */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="overflow-hidden rounded-3xl bg-zinc-900 p-8 text-white">
            <Store className="h-8 w-8 text-emerald-400" />
            <h3 className="mt-4 font-display text-2xl font-bold">For Businesses</h3>
            <p className="mt-2 text-zinc-300">
              Find local creators without spending hours searching Instagram and TikTok.
            </p>
            <Button data-testid="cta-business" onClick={() => navigate("/auth?mode=register&role=business")} className="mt-6 rounded-full bg-emerald-500 hover:bg-emerald-600">
              Join as a business
            </Button>
          </div>
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 to-indigo-800 p-8 text-white">
            <Camera className="h-8 w-8 text-amber-300" />
            <h3 className="mt-4 font-display text-2xl font-bold">For Creators</h3>
            <p className="mt-2 text-indigo-100">
              Get discovered by businesses near you and turn your audience into local opportunities.
            </p>
            <Button data-testid="cta-creator" onClick={() => navigate("/auth?mode=register&role=creator")} className="mt-6 rounded-full bg-white text-indigo-700 hover:bg-indigo-50">
              Join as a creator
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
